import React, { Suspense } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/browser'
import { Router } from '@fs/zion-router'
import { sentryDSN, mergeSentryConfig, appPath } from '@fs/zion-config'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { AppLayout } from '@fs/zion-ui'
import App from './App'
import * as serviceWorker from './serviceWorker'
import './locales'
import PageLoading from './shared/components/PageLoading'
import { QueryClientProvider } from './shared/providers/QueryClientProvider'
import { APP_BASE_PATH, APP_PATHS } from './shared/config'

// Enable local feature flag development using the file "dev.flags.ts"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      strictFiltering: true,
      dsn: sentryDSN,
      ignoreErrors: [
        /StorageQuotaExceeded/, // Non-critical localstorage errors (zion-cache custom error class)
        /Java object is gone/, // Android WebView-specific
        /xbrowser is not defined/, // Third-party auto-fill extensions/password managers
      ],
      denyUrls: [
        /^file:\/\//, // Errors from downloaded/local HTML files
      ],
    })
  )
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

const famousPath = `${APP_BASE_PATH}${APP_PATHS.famous}`
const signOutReturnUrl = window.location.pathname.includes(famousPath) ? famousPath : `${APP_BASE_PATH}/`

const AppRoot = (): JSX.Element => {
  return (
    <Router basename={base}>
      <Root header={{ signOutReturnUrl }} analytics={!navigator.userAgent.includes('prerender')}>
        <AppLayout fullWidth>
          <App />
        </AppLayout>
      </Root>
    </Router>
  )
}

// add any app logic here, languages and translations are configured and available here
const FrontierWrapper = (): JSX.Element => {
  return (
    <QueryClientProvider>
      <AppRoot />
    </QueryClientProvider>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = (): JSX.Element => {
  return (
    <React.StrictMode>
      <Suspense fallback={<PageLoading />}>
        <FrontierBaseProviders mockFlags={mockFlags} LoadingFallback={PageLoading} mockActivities={mockActivities}>
          <FrontierWrapper />
        </FrontierBaseProviders>
      </Suspense>
    </React.StrictMode>
  )
}

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
